import React, { useEffect, useRef, useState } from 'react';
import styles from '../chatAssist.module.scss';
import PromptContainer from '../PromptContainer/PromptContainer';
import MessagesContainer from './MessagesContainerV2/MessagesContainer';
import ChatInput from '../ChatInput/ChatInput';
import IconButton from '../IconButton/IconButton';
import Loader from '../Loader/Loader';
import {
  msgInfo,
  errorResponse,
  IntroducingKai,
  IntroducingNea,
  tipsTextKai,
  tipsTextNea,
} from '../constants';
import { useSalesAssistStore } from 'features/chatAssist/ChatAssist.store';
import { ArrowDownward } from '@material-ui/icons';
import classNames from 'classnames';
import { askQuestionsListenEvents } from 'features/chatAssist/ChatAssist.services';
import { useLiveTyping, useWhichLabelsToShow } from 'features/chatAssist/ChatAssistHook';
import Organization from 'infrastructure/Organization';
import ChatAssistV3 from './NewAPIIntegration/ChatAssistV3';

const ChatAssistV2 = ({ useNewApi = false }) => {
  const org_id = Organization.getSelectedOrg()?.id;
  const messagesContainerRef = useRef(null);
  const firstQuestionRef = useRef(null);
  const firstResponseRef = useRef(null);

  const [value, setValue] = useState(''); //input value
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false); //to handle when endpoint still sending responses
  const [isWaiting, setIsWaiting] = useState(false); //to handle the loading status of the request

  const addID = useSalesAssistStore((s) => s?.setId);
  const id = useSalesAssistStore((state) => state?.session_id);

  const messages = useSalesAssistStore((state) => state?.messages);
  const addUserMessage = useSalesAssistStore((state) => state?.addUserMessage);
  const startAIMessage = useSalesAssistStore((state) => state?.startAIMessage);
  const addToLastAIMessage = useSalesAssistStore((state) => state?.addToLastAIMessage);

  const { displayedLines, isTyping, appendChunk, reset } = useLiveTyping({ speed: 50 });
  const chatAgent = useWhichLabelsToShow();

  const isJustSpaces = (m) => m.trim() === '';

  const onChange = (e) => {
    e.preventDefault();
    setValue(e.target.value);
  };

  const handleNewMessages = async (message) => {
    if (isJustSpaces(message)) return;

    addUserMessage(message);
    startAIMessage();
    setValue('');
    setIsWaiting(true);
    setIsStreaming(true);

    await askQuestionsListenEvents({
      org_id,
      question: message,
      session_id: id,
      onMessage: (data) => {
        appendChunk(data);
        addToLastAIMessage(data);
      },
      onError: (err) => {
        console.error('Stream error:', err);
        setIsStreaming(false);
        reset();
        addToLastAIMessage(errorResponse?.text);
      },
      onComplete: () => {
        reset();
        setIsStreaming(false);
      },
      onLoading: () => {
        setIsWaiting(false);
      },
      onSaveId: (data) => {
        addID(data);
      },
    });
  };

  const handleSendMessage = () => {
    handleNewMessages(value, msgInfo?.sender);
  };

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (container) {
      const { scrollTop } = container;

      if (scrollTop < 0) {
        setShowScrollButton(true);
      } else {
        setShowScrollButton(false);
      }
    }
  };

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    const scrollHandler = () => handleScroll();
    if (container) {
      container.addEventListener('scroll', scrollHandler);
      handleScroll();
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', scrollHandler);
      }
    };
  }, []);

  useEffect(() => {
    if (messages.length === 0) return;

    const lastMessage = messages[0];

    if (lastMessage.type === msgInfo.sender && firstQuestionRef.current) {
      firstQuestionRef.current.scrollIntoView({ block: 'start' });
    } else if (lastMessage.type === msgInfo.recipient && firstResponseRef.current) {
      firstResponseRef.current.scrollIntoView({ block: 'start' });
    }
  }, [messages]);

  if (useNewApi) return <ChatAssistV3 chatAgent={chatAgent} />;

  return (
    <div className={styles.chatContainer}>
      <div
        className={classNames(styles.messagesContainer, {
          [styles.hidden]: Object.keys(messages).length === 0,
        })}
        ref={messagesContainerRef}
      >
        <MessagesContainer
          messages={messages}
          firstQuestionRef={firstQuestionRef}
          firstResponseRef={firstResponseRef}
          isWaiting={isWaiting}
          displayedLines={displayedLines}
          isTyping={isTyping}
        />
      </div>
      {showScrollButton && (
        <button className={styles.scrollButton} onClick={scrollToBottom}>
          <ArrowDownward />
        </button>
      )}
      {Object.keys(messages).length === 0 && (
        <div className={styles.introContainer}>
          <div className={styles.agentContainer}>
            <div>
              <p className={styles.title}>
                {chatAgent === 'Kai' ? '⚡ KAI — Sales Rep' : '🤖 Nea — SalesManager'} Co-Pilot
              </p>
              <p className={styles.text}>{chatAgent === 'Kai' ? IntroducingKai : IntroducingNea}</p>
            </div>
          </div>
          <div className={styles.promptContainer}>
            {chatAgent === 'Kai'
              ? tipsTextKai.map((item, index) => (
                  <PromptContainer
                    key={index}
                    title={item?.title}
                    text={item?.text}
                    image={item?.image}
                  />
                ))
              : tipsTextNea.map((item, index) => (
                  <PromptContainer
                    key={index}
                    title={item?.title}
                    text={item?.text}
                    image={item?.image}
                  />
                ))}
          </div>
        </div>
      )}
      {(isWaiting || isStreaming) && (
        <div className={styles.loadMessageContainer}>
          <Loader />
        </div>
      )}
      <div className={styles.inputContainer}>
        <ChatInput
          value={value}
          onChange={onChange}
          onKeyPress={handleSendMessage}
          disabled={isWaiting || isStreaming}
          placeholder={`${chatAgent} is your shortcut to SalesTable insights. Ask or search anything.`}
        />
        <IconButton
          onClick={handleSendMessage}
          disabled={isWaiting || value === '' || isJustSpaces(value)}
        />
      </div>
    </div>
  );
};

export default ChatAssistV2;
